import { handleError } from '@anchorage/sentry/errors';

// The dashboard's /logout is a bare fetch with no timeout, and a hung request
// would never settle — pinning the client to the page it is trying to leave.
export const LOGOUT_TIMEOUT_MS = 5_000;

// Apollo calls its 401 handler once per errored operation, so one denial or
// expiry arrives N times. After the first logout clears the cookie, a denial's
// later 401s lose their reason header and look like an expired session.
let redirecting = false;

/** Test-only: clears the latch so each case starts from a client still in place. */
export const resetRedirectAfterLogout = () => {
  redirecting = false;
};

// 400 means there was no session cookie to clear.
const isSessionAlreadyGone = (response: Response) => response.status === 400;

const rejectAfter = (ms: number) => {
  let timer: ReturnType<typeof setTimeout>;
  const promise = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`Timed out clearing the session after ${ms}ms`)),
      ms,
    );
  });
  // Cancelled on the happy path so a settled race leaves behind neither a
  // pending timer nor a rejection with nothing left to catch it.
  return { promise, cancel: () => clearTimeout(timer) };
};

/**
 * Drops the client's session, then runs `navigate` once per page load, cleared
 * or not. A logout that fails with anything but 400 or outlasts
 * `LOGOUT_TIMEOUT_MS` is reported.
 *
 * Takes both as arguments because the session API and the route table live in
 * frontoffice packages this one must not depend on.
 */
export const redirectAfterLogout = async (
  logout: () => Promise<Response>,
  navigate: () => void,
) => {
  if (redirecting) {
    return;
  }
  redirecting = true;

  const deadline = rejectAfter(LOGOUT_TIMEOUT_MS);

  try {
    const response = await Promise.race([logout(), deadline.promise]);
    if (!response.ok && !isSessionAlreadyGone(response)) {
      throw new Error(
        `Unable to clear the session with ${response.status} error ${response.statusText}`,
      );
    }
  } catch (error) {
    // Still the right destination with the cookie intact, so report and go.
    handleError(error as Error);
  } finally {
    deadline.cancel();
  }

  navigate();
};

/**
 * Sends a denied client to the Access Denied page, dropping their session on
 * the way out: keeping it makes the login page's claim_session re-deny and
 * bounce them straight back here.
 */
export const redirectToAccessDenied = (
  logout: () => Promise<Response>,
  navigate: () => void,
) => redirectAfterLogout(logout, navigate);
